import { useState, useEffect, useCallback } from "react";
import { PanelRightOpen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ZenCarouselCanvas } from "./zen-carousel-canvas";
import { ZenCarouselSidebar } from "./zen-carousel-sidebar";
import { ZenCarouselHeader } from "./zen-carousel-header";
import type { ZenFindingItem } from "./zen-types";

export interface ZenFindingsCarouselProps {
  findings: ZenFindingItem[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  filterScope?: string;
  canEdit?: boolean;
  hasLinkedRepo?: boolean;
  isTrackerConfigured?: boolean;
  activeProvider?: string | null;
  exportConfig?: unknown;
  createIssuePending?: boolean;
  onCreateIssue?: (finding: ZenFindingItem) => void;
  onLinkExisting?: (canonicalId: string) => void;
  onViewInTrace?: () => void;
}

export function ZenFindingsCarousel({
  findings,
  initialIndex = 0,
  open,
  onOpenChange,
  projectId,
  filterScope,
  canEdit = true,
  hasLinkedRepo = false,
  isTrackerConfigured = false,
  activeProvider = null,
  exportConfig,
  createIssuePending = false,
  onCreateIssue,
  onLinkExisting,
  onViewInTrace,
}: ZenFindingsCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showHighlights, setShowHighlights] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [focusMode, setFocusMode] = useState<"page" | "target">("page");

  // Sync initialIndex when modal opens
  useEffect(() => {
    if (open) {
      const clamped = Math.max(0, Math.min(initialIndex, findings.length - 1));
      setCurrentIndex(clamped);
      setShowHighlights(true);
      setFocusMode("page");
    }
  }, [open, initialIndex, findings.length]);

  // Track HTML5 display fullscreen state
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const total = findings.length;
  const currentFinding = findings[currentIndex] ?? null;

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < total - 1 ? prev + 1 : prev));
    setFocusMode("page");
  }, [total]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
    setFocusMode("page");
  }, []);

  const toggleFocusMode = useCallback(() => {
    setFocusMode((prev) => (prev === "target" ? "page" : "target"));
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  const handleClose = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
    onOpenChange(false);
  }, [onOpenChange]);

  // Keyboard Shortcuts: j/k or arrows, h (highlights), z (zoom focus), f (fullscreen), [ or b (sidebar)
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }

      if (e.key === "ArrowRight" || e.key === "j" || e.key === "J") {
        e.preventDefault();
        handleNext();
      } else if (e.key === "ArrowLeft" || e.key === "k" || e.key === "K") {
        e.preventDefault();
        handlePrev();
      } else if (e.key === "h" || e.key === "H") {
        e.preventDefault();
        setShowHighlights((prev) => !prev);
      } else if (e.key === "z" || e.key === "Z") {
        if (currentFinding?.targetBox) {
          e.preventDefault();
          toggleFocusMode();
        }
      } else if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === "[" || e.key === "b" || e.key === "B") {
        e.preventDefault();
        setShowSidebar((prev) => !prev);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    open,
    handleNext,
    handlePrev,
    toggleFocusMode,
    toggleFullscreen,
    currentFinding,
  ]);

  if (!currentFinding || total === 0) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="fixed inset-0 top-0 left-0 z-50 flex h-dvh max-h-none w-screen max-w-none sm:max-w-none translate-x-0 translate-y-0 flex-col gap-0 p-0 sm:p-0 rounded-none border-0 shadow-none bg-background overflow-hidden duration-100"
        showCloseButton={false}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Zen Findings Review</DialogTitle>
          <DialogDescription>
            True fullscreen walkthrough inspection and finding review carousel
          </DialogDescription>
        </DialogHeader>

        {/* Top Header Navigation Strip */}
        <ZenCarouselHeader
          currentIndex={currentIndex}
          total={total}
          stepTitle={currentFinding.stepTitle}
          filterScope={filterScope}
          onPrev={handlePrev}
          onNext={handleNext}
          showSidebar={showSidebar}
          onToggleSidebar={() => setShowSidebar((prev) => !prev)}
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
          onClose={handleClose}
        />

        {/* 2-Column Split or Single-Column Cinema Takeover */}
        <div
          className={cn(
            "grid flex-1 min-h-0 w-full overflow-hidden transition-all duration-200",
            showSidebar ? "lg:grid-cols-[1fr_28rem]" : "grid-cols-1",
          )}
        >
          <div className="relative flex min-h-0 w-full flex-col overflow-hidden bg-muted/20">
            <ZenCarouselCanvas
              finding={currentFinding}
              showHighlights={showHighlights}
              onToggleHighlights={() => setShowHighlights((prev) => !prev)}
              focusMode={focusMode}
              onToggleFocusMode={toggleFocusMode}
            />
            {!showSidebar && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSidebar(true)}
                className="absolute bottom-4 right-4 z-20 gap-1.5 bg-background/90 backdrop-blur-xs font-semibold shadow-md"
                title="Open details panel ([ or b)"
              >
                <PanelRightOpen className="size-4" />
                <span>Show Details</span>
              </Button>
            )}
          </div>

          {showSidebar && (
            <ZenCarouselSidebar
              finding={currentFinding}
              projectId={projectId}
              canEdit={canEdit}
              hasLinkedRepo={hasLinkedRepo}
              isTrackerConfigured={isTrackerConfigured}
              activeProvider={activeProvider}
              exportConfig={exportConfig}
              createIssuePending={createIssuePending}
              onCreateIssue={onCreateIssue}
              onLinkExisting={onLinkExisting}
              onViewInTrace={onViewInTrace}
              onClose={handleClose}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
